import React, { useState } from 'react';
import jsYaml from 'js-yaml';
import Papa from 'papaparse';

// Define types for our component
interface YamlToCsvProps {}

// Function to flatten nested objects for CSV conversion
const flattenObject = (obj: any, prefix: string = ''): Record<string, any> => {
  let result: Record<string, any> = {};
  
  // Process each property
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      const newKey = prefix ? `${prefix}.${key}` : key;
      
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // Recursively flatten nested objects
        const flattened = flattenObject(value, newKey);
        result = { ...result, ...flattened };
      } else if (Array.isArray(value)) {
        // Handle arrays by joining elements with a delimiter
        result[newKey] = value.map(item => {
          if (typeof item === 'object' && item !== null) {
            return JSON.stringify(item);
          }
          return String(item);
        }).join('|');
      } else {
        // Add primitive values directly
        result[newKey] = value;
      }
    }
  }
  
  return result;
};

// Convert array of objects to CSV
const arrayToCsv = (data: any[]): string => {
  // Get all unique keys from all objects
  const allKeys = new Set<string>();
  const flattenedData: Record<string, any>[] = [];
  
  // Flatten each object and collect all possible keys
  data.forEach(item => {
    const flatItem = flattenObject(item);
    flattenedData.push(flatItem);
    Object.keys(flatItem).forEach(key => allKeys.add(key));
  });
  
  // Convert to array for Papa.unparse
  const columns = Array.from(allKeys);
  
  // Use PapaParse to convert to CSV
  return Papa.unparse({
    fields: columns,
    data: flattenedData.map(item => {
      return columns.map(col => item[col] !== undefined ? item[col] : '');
    })
  });
};

// Main component to convert YAML to CSV
const YamlToCsv: React.FC<YamlToCsvProps> = () => {
  // State for the YAML input
  const [yamlInput, setYamlInput] = useState<string>('');
  // State for the CSV output
  const [csvOutput, setCSVOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('yaml_data');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State to track if the conversion happened
  const [isCsvGenerated, setIsCsvGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for delimiter
  const [delimiter, setDelimiter] = useState<string>(',');
  // State for header treatment
  const [includeHeader, setIncludeHeader] = useState<boolean>(true);
  // State for how to handle complex objects
  const [complexObjectHandling, setComplexObjectHandling] = useState<string>('flatten');
  // State for handling null values
  const [nullValue, setNullValue] = useState<string>('');

  // Handle YAML input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setYamlInput(e.target.value);
    setError('');
    setIsCsvGenerated(false);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    const exampleYaml = `# Example YAML data
- name: John Doe
  age: 30
  location:
    city: New York
    country: USA
  skills:
    - JavaScript
    - TypeScript
    - React
  active: true
- name: Jane Smith
  age: 28
  location:
    city: London
    country: UK
  skills:
    - Python
    - Data Analysis
    - Machine Learning
  active: false
- name: Bob Johnson
  age: 35
  location:
    city: Toronto
    country: Canada
  skills:
    - Java
    - Spring
    - SQL
  active: true`;

    setYamlInput(exampleYaml);
    setError('');
    setIsCsvGenerated(false);
    setCSVOutput('');
  };

  // Validate YAML
  const validateYaml = (yamlString: string): [boolean, any] => {
    try {
      const parsed = jsYaml.load(yamlString);
      return [true, parsed];
    } catch (e) {
      return [false, e];
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setYamlInput('');
    setCSVOutput('');
    setError('');
    setFileName('yaml_data');
    setDelimiter(',');
    setIncludeHeader(true);
    setComplexObjectHandling('flatten');
    setNullValue('');
    setIsCsvGenerated(false);
  };

  // Beautify/Format YAML
  const beautifyYaml = () => {
    try {
      if (!yamlInput.trim()) {
        setError('Please enter YAML data to format');
        return;
      }
      
      const [isValid, parsed] = validateYaml(yamlInput);
      
      if (!isValid) {
        setError(`Invalid YAML format: ${parsed instanceof Error ? parsed.message : String(parsed)}`);
        return;
      }
      
      // Convert back to YAML with consistent formatting
      const beautified = jsYaml.dump(parsed, {
        indent: 2,
        lineWidth: -1,
        noRefs: true,
        noCompatMode: true
      });
      
      setYamlInput(beautified);
      setError('');
    } catch (e) {
      setError(`Error formatting YAML: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Convert YAML to CSV
  const convertYamlToCsv = () => {
    setError('');
    setIsLoading(true);
    setIsCsvGenerated(false);
    setCSVOutput('');
    
    if (!yamlInput.trim()) {
      setError('Please enter YAML data');
      setIsLoading(false);
      return;
    }

    try {
      const [isValid, parsedYaml] = validateYaml(yamlInput);
      
      if (!isValid) {
        setError(`Invalid YAML format: ${parsedYaml instanceof Error ? parsedYaml.message : String(parsedYaml)}`);
        setIsLoading(false);
        return;
      }
      
      let csvData = '';
      
      // Handle different types of YAML structures
      if (Array.isArray(parsedYaml)) {
        // Array of objects is the ideal format for CSV
        csvData = arrayToCsv(parsedYaml);
      } else if (typeof parsedYaml === 'object' && parsedYaml !== null) {
        // Single object needs to be wrapped in an array
        csvData = arrayToCsv([parsedYaml]);
      } else {
        // Primitive values don't make sense for CSV
        throw new Error('YAML content must be an object or array of objects to convert to CSV');
      }
      
      // Apply CSV options
      if (delimiter !== ',') {
        // Re-parse with custom delimiter
        const parsed = Papa.parse(csvData, { header: true });
        csvData = Papa.unparse(parsed.data, {
          delimiter: delimiter,
          header: includeHeader
        });
      }
      
      setCSVOutput(csvData);
      setIsCsvGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error converting YAML to CSV: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy CSV to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(csvOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download CSV file
  const downloadCsv = () => {
    const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'yaml_data'}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">YAML to CSV Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert YAML data to CSV format. Paste your YAML data below to generate a CSV file.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="yamlInput" className="font-medium text-gray-700">
                YAML Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyYaml}
                >
                  Format YAML
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setYamlInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="yamlInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={yamlInput}
              onChange={handleInputChange}
              placeholder="Paste your YAML data here, e.g.,
- name: John
  age: 30
  city: New York
- name: Jane
  age: 28
  city: Boston"
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={handleFileNameChange}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="delimiter" className="block text-sm font-medium text-gray-700 mb-1">
                    Delimiter
                  </label>
                  <select
                    id="delimiter"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={delimiter}
                    onChange={(e) => setDelimiter(e.target.value)}
                  >
                    <option value=",">Comma (,)</option>
                    <option value=";">Semicolon (;)</option>
                    <option value="\t">Tab</option>
                    <option value="|">Pipe (|)</option>
                  </select>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includeHeader"
                    checked={includeHeader}
                    onChange={(e) => setIncludeHeader(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="includeHeader" className="ml-2 block text-sm text-gray-700">
                    Include Header Row
                  </label>
                </div>
                
                <div>
                  <label htmlFor="complexObjectHandling" className="block text-sm font-medium text-gray-700 mb-1">
                    Complex Object Handling
                  </label>
                  <select
                    id="complexObjectHandling"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={complexObjectHandling}
                    onChange={(e) => setComplexObjectHandling(e.target.value)}
                  >
                    <option value="flatten">Flatten with Dot Notation</option>
                    <option value="stringify">Convert to JSON String</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="nullValue" className="block text-sm font-medium text-gray-700 mb-1">
                    Null/Undefined Value Replacement
                  </label>
                  <input
                    type="text"
                    id="nullValue"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={nullValue}
                    onChange={(e) => setNullValue(e.target.value)}
                    placeholder="Leave empty for blank"
                  />
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertYamlToCsv}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to CSV'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              CSV Output
            </label>
            
            {isCsvGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy CSV'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadCsv}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isCsvGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    CSV output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isCsvGenerated && (
              <div className="flex-grow flex flex-col">                
                <div className="flex-grow flex flex-col">
                  <textarea
                    id="csvOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={csvOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isCsvGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">YAML Successfully Converted to CSV!</p>
              </div>
              <p className="text-sm">
                You can now copy or download the generated CSV for use in your applications.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About YAML to CSV Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool transforms your YAML data into CSV (Comma-Separated Values) format. YAML's hierarchical structure is flattened using dot notation 
              to represent nested objects, making it suitable for tabular CSV representation. Arrays are handled by joining elements with a pipe delimiter.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Smart Conversion</strong> - Handles nested objects and arrays intelligently</li>
              <li><strong>Customizable Delimiters</strong> - Choose between comma, semicolon, tab, or pipe</li>
              <li><strong>Header Row Control</strong> - Option to include or exclude column headers</li>
              <li><strong>Null Value Handling</strong> - Specify how null or undefined values should appear</li>
              <li><strong>Error Handling</strong> - Clear error messages for invalid YAML input</li>
              <li><strong>Easy Download</strong> - Save the converted CSV as a file with one click</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Best Formats for YAML Input</h3>
            <p className="text-gray-600">
              The most suitable YAML format for CSV conversion is an array of objects with similar structure, for example:
            </p>
            <pre className="bg-gray-100 p-3 rounded-md text-sm mt-2 overflow-x-auto">
{`- name: John
  age: 30
  city: New York
- name: Jane
  age: 28
  city: Boston`}
            </pre>
            <p className="text-gray-600 mt-2">
              This will produce a clean CSV with columns for name, age, and city. Nested objects will be flattened using dot notation.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format YAML" button to check if your YAML is valid before conversion</li>
              <li>For best results, ensure all objects in an array have similar structure</li>
              <li>Complex nested data structures may result in many columns when flattened</li>
              <li>Arrays will be converted to pipe-separated values in a single cell</li>
              <li>Consider using semicolon or tab delimiters if your data contains commas</li>
              <li>Use the example button to see a properly formatted YAML example</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Handling Complex Data</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Nested objects are flattened using dot notation (e.g., <code>address.city</code>)</li>
              <li>Arrays are converted to pipe-separated strings (e.g., <code>item1|item2|item3</code>)</li>
              <li>Inconsistent object structures might result in empty cells for some rows</li>
              <li>Very complex hierarchical data may not be ideal for CSV format</li>
              <li>CSV is inherently a flat format, so deeply nested structures will lose some hierarchy</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YamlToCsv;